import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, User, Shield, Eye, EyeOff, ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo-transparent.png";
const BG_IMG = "https://images.unsplash.com/photo-1605296867724-fa87a8ef53fd?w=1200&q=40";

const pageVariants = {
  initial: { opacity: 0, scale: 0.96, filter: "blur(8px)" },
  animate: { opacity: 1, scale: 1, filter: "blur(0px)", transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, scale: 0.96, filter: "blur(8px)", transition: { duration: 0.3 } }
};

function FloatingInput({ type = "text", placeholder, value, onChange, testId, icon: Icon, right }) {
  const [focused, setFocused] = useState(false);
  const active = focused || value;
  return (
    <motion.div
      className={`relative rounded-xl border transition-all duration-300 ${focused ? "border-gg-red/50 shadow-[0_0_20px_rgba(255,59,48,0.1)]" : "border-white/10 hover:border-white/20"} bg-white/[0.03]`}
      whileTap={{ scale: 0.995 }}
    >
      <input
        type={type} value={value} onChange={onChange}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        data-testid={testId}
        className="w-full bg-transparent text-white h-13 px-4 pt-5 pb-2 text-sm outline-none peer"
        required
      />
      <span className={`absolute left-4 transition-all duration-200 pointer-events-none ${active ? "top-2 text-[9px] tracking-widest uppercase" : "top-1/2 -translate-y-1/2 text-sm"} ${focused ? "text-gg-red" : "text-zinc-500"}`}>
        {placeholder}
      </span>
      {right && <div className="absolute right-3 top-1/2 -translate-y-1/2">{right}</div>}
    </motion.div>
  );
}

export default function LoginPage() {
  const [mode, setMode] = useState("choose");
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gg-black relative overflow-hidden flex items-center justify-center px-4 py-8">
      {/* Background */}
      <div className="absolute inset-0">
        <img src={BG_IMG} alt="" className="w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,59,48,0.06)_0%,transparent_70%)]" />
      </div>

      {/* Animated grain overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")" }} />

      <AnimatePresence mode="wait">
        {mode === "choose" && <ChooseRole key="choose" onSelect={setMode} />}
        {mode === "client-login" && <ClientLoginForm key="client" setMode={setMode} navigate={navigate} />}
      </AnimatePresence>
    </div>
  );
}

function ChooseRole({ onSelect }) {
  const navigate = useNavigate();
  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="w-full max-w-sm text-center relative z-10">
      <motion.img src={LOGO_URL} alt="GG Fitness" className="h-14 mx-auto mb-6"
        initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }} />

      <motion.h1 className="font-heading text-3xl font-black uppercase tracking-tighter mb-1"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}>
        Welcome Back
      </motion.h1>
      <motion.p className="text-zinc-500 text-sm mb-8"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
        Choose your portal
      </motion.p>

      <div className="space-y-3">
        <motion.button onClick={() => onSelect("client-login")} data-testid="login-client-option"
          initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35, type: "spring", stiffness: 300, damping: 24 }}
          whileHover={{ scale: 1.02, borderColor: "rgba(255,59,48,0.5)" }} whileTap={{ scale: 0.98 }}
          className="w-full flex items-center gap-4 p-5 rounded-2xl bg-white/[0.03] backdrop-blur border border-white/10 transition-all group cursor-pointer">
          <motion.div className="w-13 h-13 rounded-xl bg-gg-red/10 flex items-center justify-center"
            whileHover={{ rotate: [0, -5, 5, 0] }} transition={{ duration: 0.4 }}>
            <User size={24} className="text-gg-red" />
          </motion.div>
          <div className="text-left flex-1">
            <span className="font-heading text-sm font-bold uppercase tracking-wide block text-white">Member Login</span>
            <span className="text-zinc-500 text-xs">Access your dashboard & plans</span>
          </div>
          <ArrowRight size={18} className="text-zinc-700 group-hover:text-gg-red group-hover:translate-x-1 transition-all duration-300" />
        </motion.button>

        <motion.button onClick={() => navigate("/admin")} data-testid="login-admin-option"
          initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.45, type: "spring", stiffness: 300, damping: 24 }}
          whileHover={{ scale: 1.02, borderColor: "rgba(255,255,255,0.2)" }} whileTap={{ scale: 0.98 }}
          className="w-full flex items-center gap-4 p-5 rounded-2xl bg-white/[0.03] backdrop-blur border border-white/10 transition-all group cursor-pointer">
          <motion.div className="w-13 h-13 rounded-xl bg-white/5 flex items-center justify-center"
            whileHover={{ rotate: [0, -5, 5, 0] }} transition={{ duration: 0.4 }}>
            <Shield size={24} className="text-zinc-400" />
          </motion.div>
          <div className="text-left flex-1">
            <span className="font-heading text-sm font-bold uppercase tracking-wide block text-white">Trainer Login</span>
            <span className="text-zinc-500 text-xs">Management & CMS panel</span>
          </div>
          <ArrowRight size={18} className="text-zinc-700 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
        </motion.button>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
        <Link to="/home" className="inline-block mt-8 text-zinc-600 text-xs hover:text-white transition-colors duration-300">Back to Website</Link>
      </motion.div>
    </motion.div>
  );
}

function ClientLoginForm({ setMode, navigate }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [stayLogged, setStayLogged] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/client/login`, { email, password, remember_me: stayLogged });
      const store = stayLogged ? localStorage : sessionStorage;
      store.setItem("gg_client_token", data.token);
      store.setItem("gg_client_name", data.name);
      if (!stayLogged) localStorage.setItem("gg_client_session", "1");
      toast.success(`Welcome back, ${data.name}!`);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="w-full max-w-sm relative z-10">
      <motion.img src={LOGO_URL} alt="GG Fitness" className="h-14 mx-auto mb-6"
        initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} />

      <motion.h1 className="font-heading text-3xl font-black uppercase tracking-tighter text-center mb-1"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        Member Login
      </motion.h1>
      <motion.p className="text-zinc-500 text-sm text-center mb-8"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}>
        Enter your credentials
      </motion.p>

      <AnimatePresence>
        {error && (
          <motion.p initial={{ opacity: 0, y: -10, height: 0 }} animate={{ opacity: 1, y: 0, height: "auto" }} exit={{ opacity: 0, y: -10, height: 0 }}
            className="text-red-400 text-sm text-center mb-4 bg-red-500/10 rounded-xl py-2.5 px-4 border border-red-500/20" data-testid="login-error">
            {error}
          </motion.p>
        )}
      </AnimatePresence>

      <form onSubmit={handleLogin} className="space-y-4" data-testid="client-login-form">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <FloatingInput type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} testId="client-email" />
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
          <FloatingInput type={showPw ? "text" : "password"} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} testId="client-password"
            right={
              <button type="button" onClick={() => setShowPw(!showPw)} className="text-zinc-600 hover:text-white transition-colors">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }
          />
        </motion.div>

        <motion.label initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          className="flex items-center gap-2.5 cursor-pointer select-none group" data-testid="client-stay-logged">
          <div className={`w-4 h-4 rounded border transition-all duration-200 flex items-center justify-center ${stayLogged ? "bg-gg-red border-gg-red" : "border-white/20 group-hover:border-white/40"}`}>
            {stayLogged && <motion.svg initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12"><path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></motion.svg>}
          </div>
          <input type="checkbox" checked={stayLogged} onChange={(e) => setStayLogged(e.target.checked)} className="hidden" />
          <span className="text-zinc-500 text-xs group-hover:text-zinc-300 transition-colors">Stay logged in</span>
        </motion.label>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
          <Button type="submit" disabled={loading} data-testid="client-login-submit"
            className="w-full bg-gg-red hover:bg-gg-red-hover text-white rounded-xl h-13 font-heading uppercase tracking-widest text-sm relative overflow-hidden group disabled:opacity-50">
            <motion.span whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="relative z-10 flex items-center justify-center gap-2">
              {loading ? <Loader2 size={18} className="animate-spin" /> : "Login"}
              {!loading && <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
            </motion.span>
            <div className="absolute inset-0 bg-gradient-to-r from-gg-red to-red-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </Button>
        </motion.div>
      </form>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }} className="text-center mt-5">
        <p className="text-zinc-700 text-[10px]">Forgot password? Contact your trainer to reset it.</p>
      </motion.div>

      <motion.button onClick={() => setMode("choose")}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
        whileHover={{ x: -3 }} whileTap={{ scale: 0.95 }}
        className="flex items-center gap-1 mx-auto mt-5 text-zinc-600 text-xs hover:text-white transition-colors">
        <ChevronLeft size={14} /> Back
      </motion.button>
    </motion.div>
  );
}
